import { useProduct } from "@/features/product/product-context";
import { useMarkets } from "@/domains/markets";
import { MARKETS } from "@/solana/constants";
import { useMemo } from "react";

// LocalStorage cache key for markets data (matches markets-provider.tsx)
const MARKETS_CACHE_KEY = 'nordex_markets_cache';

/**
 * Hook to get the correct precision for the current market.
 * 
 * Returns the static precision values from the product configuration.
 * 
 * @returns Object with quotePrecision and basePrecision
 * 
 * @example
 * ```tsx
 * function PriceDisplay() {
 *   const { quotePrecision } = usePricePrecision();
 *   return <div>${formatCurrency(price, quotePrecision)}</div>;
 * }
 * ```
 */
export function usePricePrecision() {
  const { product } = useProduct();
  
  const quotePrecision = useMemo(() => {
    // Return the static precision from product config
    return product?.quote.precision ?? 2;
  }, [product]);
  
  return {
    quotePrecision,
    basePrecision: product?.base.precision ?? 4,
  };
}

/**
 * Hook to get market precision for any symbol.
 * 
 * Useful for components that display multiple markets (e.g., positions panel)
 * where we can't rely on the current product context.
 * 
 * Data source priority:
 * 1. Dynamic markets from backend (source of truth)
 * 2. Hardcoded fallback constants
 * 3. Default values (quotePrecision: 2, basePrecision: 4)
 * 
 * @param symbol - Market symbol (e.g., "BTC-USD")
 * @returns Object with quotePrecision and basePrecision
 * 
 * @example
 * ```tsx
 * function PositionPrice({ symbol, price }: { symbol: string; price: number }) {
 *   const { quotePrecision } = useMarketPrecision(symbol);
 *   return <div>${formatCurrency(price, quotePrecision)}</div>;
 * }
 * ```
 */
export function useMarketPrecision(symbol: string) {
  const { markets } = useMarkets();
  
  return useMemo(() => {
    // 1. Try dynamic markets from backend (source of truth)
    const market = markets.find(m => m.symbol === symbol);
    if (market) {
      return {
        quotePrecision: market.quotePrecision,
        basePrecision: market.basePrecision,
      };
    }
    
    // 2. Fallback to hardcoded constants
    const hardcoded = MARKETS[symbol as keyof typeof MARKETS];
    if (hardcoded) {
      return {
        quotePrecision: hardcoded.quotePrecision,
        basePrecision: hardcoded.basePrecision,
      };
    }
    
    // 3. Default fallback
    return {
      quotePrecision: 2,
      basePrecision: 4,
    };
  }, [markets, symbol]);
}

/**
 * Non-hook function to get market precision for any symbol.
 * 
 * Works outside of React component lifecycle (e.g., in loops, callbacks).
 * Uses localStorage cache → hardcoded constants → defaults as fallback.
 * 
 * @param symbol - Market symbol (e.g., "BTC-USD")
 * @returns Object with quotePrecision and basePrecision
 * 
 * @example
 * ```tsx
 * // In a loop rendering fills
 * fills.map(fill => {
 *   const { quotePrecision } = getMarketPrecision(fill.market);
 *   return formatCurrency(fill.price, quotePrecision);
 * });
 * ```
 */
export function getMarketPrecision(symbol: string): { quotePrecision: number; basePrecision: number } {
  // 1. Try localStorage cache (set by MarketsProvider)
  try {
    const cached = localStorage.getItem(MARKETS_CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      const market = data.markets?.find((m: { symbol: string; quotePrecision: number; basePrecision: number }) => 
        m.symbol === symbol
      );
      if (market) {
        return {
          quotePrecision: market.quotePrecision,
          basePrecision: market.basePrecision,
        };
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  
  // 2. Fallback to hardcoded constants
  const hardcoded = MARKETS[symbol as keyof typeof MARKETS];
  if (hardcoded) {
    return {
      quotePrecision: hardcoded.quotePrecision,
      basePrecision: hardcoded.basePrecision,
    };
  }
  
  // 3. Default fallback
  return {
    quotePrecision: 2,
    basePrecision: 4,
  };
}

